import { AppstoreOutlined, LogoutOutlined } from '@ant-design/icons';
import { canAccessAdmin } from '@cult/domain';
import type { PublicUser } from '@cult/schemas';
import { useNavigate } from '@tanstack/react-router';
import { Dropdown, Flex, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { RoleChip, ThemeSwitcher, UserAvatar } from '../../../../components';
import { useLogout } from '../../hooks';

const { Text } = Typography;

type MenuItem = NonNullable<MenuProps['items']>[number];

type Props = {
  user: PublicUser;
};

export const UserMenu = ({ user }: Props) => {
  const logout = useLogout();
  const navigate = useNavigate();

  const adminItems: MenuItem[] = canAccessAdmin(user.role)
    ? [
        {
          key: 'admin',
          icon: <AppstoreOutlined />,
          label: 'Admin panel',
          onClick: () => navigate({ to: '/admin' }),
        },
        { type: 'divider' },
      ]
    : [];

  const items: MenuItem[] = [
    {
      key: 'header',
      label: (
        <div style={{ minWidth: 220, padding: '4px 0' }}>
          <Text strong style={{ display: 'block' }}>
            {user.displayName}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {user.email}
          </Text>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    ...adminItems,
    {
      key: 'theme',
      label: (
        <Flex align="center" justify="space-between" gap={12}>
          <Text type="secondary">Theme</Text>
          <ThemeSwitcher size="small" />
        </Flex>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      onClick: () => {
        logout.mutate(undefined, {
          onSettled: () => navigate({ to: '/login' }),
        });
      },
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <Space style={{ cursor: 'pointer' }}>
        <UserAvatar avatarUrl={user.avatarUrl} size="small" />
        <Text>{user.displayName}</Text>
        <RoleChip variant={user.role} />
      </Space>
    </Dropdown>
  );
};
